// src/components/common/CvConvertModal.jsx
// Modal générique "Convertir CV" (extraction IA → édition → enregistrement CV Wikeys).
// Utilisé par AODetail (candidats) et ResourceDetail (ressources internes).
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import api, { apiLong } from '../../services/api';
import { extractError } from '../../context/AuthContext';
import {
  X, Upload, FileText, RefreshCw, CheckCircle, Briefcase, GripVertical,
  Trash2, Globe, GraduationCap, Plus, ChevronUp, ChevronDown, User, Save, Send, Download,
} from 'lucide-react';

/**
 * @param {object} candidate - profil à convertir (full_name, title, location, cv_minio_path, cv_link)
 * @param {string} entityId - id du profil dont le serveur doit récupérer le CV original
 * @param {'candidate'|'resource'|'pipeline'} sourceType - type d'entité désignée par entityId
 * @param {string} [targetTitle] - intitulé de poste cible (ex: ao.job_title)
 * @param {()=>void} onClose
 * @param {()=>Promise} [onRescore] - callback optionnel pour relancer un scoring (contexte AO)
 * @param {boolean} [cvAvailable] - surcharge la détection d'un CV existant (ex: CV récupéré via un profil lié)
 */
export default function CvConvertModal({ candidate, entityId, sourceType, targetTitle, onClose, onRescore, cvAvailable }) {
  // Sans référence exploitable côté serveur, on ne peut rien convertir automatiquement :
  // on part directement sur l'import manuel plutôt que de produire un CV vide.
  const canAutoConvert = !!(sourceType && entityId);
  const hasExistingCV = canAutoConvert && (cvAvailable ?? !!(candidate.cv_minio_path || candidate.cv_link));
  const [step, setStep] = useState(hasExistingCV ? 'extract' : 'upload');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(hasExistingCV);
  const [cvData, setCvData] = useState(null);
  const [cvId, setCvId] = useState(null);
  const [sendModal, setSendModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [expCV, setExpCV] = useState({ personal: true, experiences: true });
  const [rescoring, setRescoring] = useState(false);
  const [translating, setTranslating] = useState(false);


  const onDrop = useCallback((accepted) => {
    if (accepted[0]) setFile(accepted[0]);
  }, []);


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'text/plain': [],
    },
    maxFiles: 1,
  });


  useEffect(() => {
    if (hasExistingCV) autoConvert();
  }, []);


  const autoConvert = async () => {
    setStep('extract');
    setLoading(true);
    try {
      // Le fichier n'est pas telecharge par le navigateur : on transmet la reference du profil
      // et c'est le serveur qui va chercher le CV (fetch() cross-origin est bloque par CORS,
      // et les URLs presignees expirent).
      const formData = new FormData();
      if (sourceType && entityId) formData.append(`${sourceType}_id`, entityId);
      if (targetTitle) formData.append('target_title', targetTitle);


      const res = await apiLong.post('/cv/convert', formData);
      setCvData(res.data.json_cv || res.data);
      setCvId(null);
      setStep('edit');
    } catch (e) {
      toast.error(extractError(e));
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };


  const handleConvert = async () => {
    if (!file) return;
    setLoading(true);
    setStep('extract');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (targetTitle) fd.append('target_title', targetTitle);
      const r = await apiLong.post('/cv/convert', fd);
      setCvData(r.data.json_cv || r.data);
      setStep('edit');
    } catch (e) {
      toast.error(extractError(e));
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    setLoading(true);
    try {
      if (cvId) {
        await api.put(`/cv/${cvId}`, { json_cv: cvData });
        toast.success('CV mis à jour');
      } else {
        const r = await api.post('/cv/save', { json_cv: cvData });
        setCvId(r.data.id);
        toast.success('CV Wikeys enregistré !');
      }
      setStep('done');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };


  const handleDownload = async () => {
    try {
      const r = await api.post(`/cv/${cvId}/regenerate-docx`);
      if (r.data?.url) window.open(r.data.url, '_blank');
      else toast.error('URL non disponible');
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleSend = async () => {
    try {
      await api.post(`/cv/${cvId}/send-to-candidate`, { candidate_email: sendEmail });
      toast.success('Email envoyé');
      setSendModal(false);
      setSendEmail('');
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleRescore = async () => {
    if (!onRescore) return;
    setRescoring(true);
    try {
      await onRescore();
      toast.success('Rescore lancé — actualisez la page pour voir le nouveau score');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setRescoring(false);
    }
  };

  const handleTranslate = async () => {
    if (!cvId) return;
    setTranslating(true);
    try {
      const r = await api.post(`/cv/${cvId}/translate`);
      if (r.data?.json_cv) {
        setCvData(r.data.json_cv);
        toast.success('CV traduit en anglais');
      }
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setTranslating(false);
    }
  };

  const setP = (k, v) => setCvData((d) => ({ ...d, personal_info: { ...d.personal_info, [k]: v } }));
  const addExp = () => setCvData((d) => ({
    ...d,
    experiences: [...(d.experiences || []), { entreprise: '', dates: '', poste: '', synopsis: '', realisations: [''], technologies: [] }],
  }));
  const updExp = (i, k, v) => setCvData((d) => {
    const e = [...d.experiences];
    e[i] = { ...e[i], [k]: v };
    return { ...d, experiences: e };
  });
  const remExp = (i) => setCvData((d) => ({ ...d, experiences: d.experiences.filter((_, j) => j !== i) }));
  const addT = (ei) => setCvData((d) => {
    const e = [...d.experiences];
    e[ei] = { ...e[ei], realisations: [...(e[ei].realisations || []), ''] };
    return { ...d, experiences: e };
  });
  const updT = (ei, ti, v) => setCvData((d) => {
    const e = [...d.experiences];
    const r = [...(e[ei].realisations || [])];
    r[ti] = v;
    e[ei] = { ...e[ei], realisations: r };
    return { ...d, experiences: e };
  });
  const remT = (ei, ti) => setCvData((d) => {
    const e = [...d.experiences];
    e[ei].realisations = e[ei].realisations.filter((_, j) => j !== ti);
    return { ...d, experiences: e };
  });
  const togCV = (k) => setExpCV((p) => ({ ...p, [k]: !p[k] }));


  const STEPS = ['upload', 'extract', 'edit', 'done'];
  const si = STEPS.indexOf(step);


  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, width: '100%', maxWidth: 860, maxHeight: '94vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>
        <div className="modal-header">
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9375rem' }}>Convertir CV — {candidate.full_name}</div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Format Wikeys{targetTitle ? ` · ${targetTitle}` : ''}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>


        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, padding: '12px 24px', borderBottom: '1px solid var(--border)', background: 'var(--gray-50)' }}>
          {[{ key: 'upload', label: 'Upload' }, { key: 'extract', label: 'Extraction IA' }, { key: 'edit', label: 'Édition' }, { key: 'done', label: 'Terminé' }].map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: si >= i ? '#3B82F6' : 'var(--gray-200)', color: si >= i ? 'white' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 700 }}>
                  {si > i ? <CheckCircle size={13} /> : i + 1}
                </div>
                <span style={{ fontSize: '0.8125rem', fontWeight: si === i ? 700 : 400, color: si === i ? '#3B82F6' : 'var(--text-muted)' }}>{s.label}</span>
              </div>
              {i < 3 && <div style={{ width: 40, height: 2, margin: '0 8px', background: si > i ? '#3B82F6' : 'var(--border)' }} />}
            </React.Fragment>
          ))}
        </div>


        <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          {step === 'upload' && (
            <div className="card">
              <div className="card-header"><span className="card-title">Uploader le CV de {candidate.full_name}</span></div>
              <div {...getRootProps()} className={`upload-zone${isDragActive ? ' drag-over' : ''}`}>
                <input {...getInputProps()} />
                <Upload size={32} color={isDragActive ? 'var(--blue-600)' : 'var(--gray-400)'} />
                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{isDragActive ? 'Déposez ici…' : 'Glissez le CV ou cliquez'}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>PDF, DOC, DOCX, TXT</div>
                <button type="button" className="btn btn-secondary btn-sm">Parcourir</button>
              </div>


              {file && (
                <div className="cv-file-selected">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileText size={18} color="var(--blue-600)" />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{file.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{(file.size / 1024).toFixed(0)} Ko</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}><X size={14} /> Retirer</button>
                    <button className="btn btn-orange" onClick={handleConvert} disabled={loading} style={{ background: '#3B82F6', borderColor: '#3B82F6' }}>
                      <RefreshCw size={15} /> Lancer l'extraction IA
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}


          {step === 'extract' && (
            <div className="card" style={{ textAlign: 'center', padding: '60px 40px' }}>
              <div className="cv-extract-anim"><div className="cv-extract-ring" /><FileText size={28} color="var(--blue-600)" /></div>
              <h3 style={{ marginTop: 24, marginBottom: 8 }}>Extraction en cours…</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>L'IA analyse le CV de {candidate.full_name}.</p>
            </div>
          )}


          {(step === 'edit' || step === 'done') && cvData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card">
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => togCV('personal')}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><User size={16} color="var(--blue-600)" /><span className="card-title">Informations personnelles</span></div>
                  {expCV.personal ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                {expCV.personal && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {[{ k: 'prenom', l: 'Prénom' }, { k: 'nom', l: 'Nom' }, { k: 'trigramme', l: 'Trigramme' }, { k: 'poste', l: 'Titre' }, { k: 'annees_experience', l: "Années d'exp." }].map(({ k, l }) => (
                      <div key={k} className="input-group">
                        <label className="input-label">{l}</label>
                        <input className="input" value={cvData.personal_info?.[k] || ''} onChange={(e) => setP(k, e.target.value)} />
                      </div>
                    ))}
                    <div className="input-group" style={{ gridColumn: '1/-1' }}>
                      <label className="input-label">Présentation</label>
                      <textarea className="textarea" rows={3} value={cvData.personal_info?.presentation || ''} onChange={(e) => setP('presentation', e.target.value)} />
                    </div>
                  </div>
                )}
              </div>


              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Briefcase size={16} color="var(--blue-600)" /><span className="card-title">Expériences</span><span className="badge badge-blue">{cvData.experiences?.length || 0}</span></div>
                  <button className="btn btn-secondary btn-sm" onClick={addExp}><Plus size={13} /> Ajouter</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {(cvData.experiences || []).map((exp, i) => (
                    <div key={i} className="cv-exp-block">
                      <div className="cv-exp-header">
                        <GripVertical size={14} color="var(--text-muted)" style={{ cursor: 'grab' }} />
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div className="input-group"><label className="input-label">Poste</label><input className="input" value={exp.poste || ''} onChange={(e) => updExp(i, 'poste', e.target.value)} /></div>
                          <div className="input-group"><label className="input-label">Entreprise</label><input className="input" value={exp.entreprise || ''} onChange={(e) => updExp(i, 'entreprise', e.target.value)} /></div>
                          <div className="input-group" style={{ gridColumn: '1/-1' }}><label className="input-label">Dates</label><input className="input" value={exp.dates || ''} onChange={(e) => updExp(i, 'dates', e.target.value)} placeholder="Jan 2022 - Présent" /></div>
                          <div className="input-group" style={{ gridColumn: '1/-1' }}><label className="input-label">Synopsis</label><input className="input" value={exp.synopsis || ''} onChange={(e) => updExp(i, 'synopsis', e.target.value)} /></div>
                        </div>
                        <button className="btn btn-danger btn-icon btn-sm" onClick={() => remExp(i)}><Trash2 size={13} /></button>
                      </div>


                      <div className="cv-tasks">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Réalisations</div>
                          <button className="btn btn-ghost btn-sm" onClick={() => addT(i)}><Plus size={12} /> Ajouter</button>
                        </div>


                        {(exp.realisations || []).map((t, ti) => (
                          <div key={ti} className="cv-task-row">
                            <div className="cv-task-dot" />
                            <input className="input cv-task-input" value={t} onChange={(e) => updT(i, ti, e.target.value)} placeholder="Description de la réalisation…" />
                            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => remT(i, ti)}><X size={12} /></button>
                          </div>
                        ))}
                      </div>


                      <div className="input-group" style={{ marginTop: 8 }}>
                        <label className="input-label" style={{ fontSize: '0.6875rem' }}>Technologies (virgule)</label>
                        <input className="input" style={{ height: 32, fontSize: '0.8125rem' }} value={(exp.technologies || []).join(', ')} onChange={(e) => updExp(i, 'technologies', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>


              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Globe size={16} color="var(--blue-600)" /><span className="card-title">Langues</span></div>
                  <button className="btn btn-secondary btn-sm" onClick={() => setCvData((d) => ({ ...d, langues: [...(d.langues || []), { langue: '', niveau: '' }] }))}><Plus size={13} /> Ajouter</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                  {(cvData.langues || []).map((l, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                      <div className="input-group" style={{ flex: 1 }}>
                        <label className="input-label">Langue</label>
                        <input className="input" value={l.langue || ''} onChange={(e) => {
                          const lng = [...cvData.langues];
                          lng[i] = { ...lng[i], langue: e.target.value };
                          setCvData((d) => ({ ...d, langues: lng }));
                        }} />
                      </div>
                      <div className="input-group" style={{ flex: 1 }}>
                        <label className="input-label">Niveau</label>
                        <select className="input select" value={l.niveau || ''} onChange={(e) => {
                          const lng = [...cvData.langues];
                          lng[i] = { ...lng[i], niveau: e.target.value };
                          setCvData((d) => ({ ...d, langues: lng }));
                        }}>
                          {['', 'Débutant', 'Intermédiaire', 'Avancé', 'Courant', 'Bilingue', 'Natif'].map((n) => <option key={n}>{n}</option>)}
                        </select>
                      </div>
                      <button className="btn btn-danger btn-icon btn-sm" style={{ marginBottom: 1 }} onClick={() => setCvData((d) => ({ ...d, langues: d.langues.filter((_, j) => j !== i) }))}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>


              <div className="card">
                <div className="card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><GraduationCap size={16} color="var(--blue-600)" /><span className="card-title">Formations</span></div>
                  <button className="btn btn-secondary btn-sm" onClick={() => setCvData((d) => ({ ...d, formations: [...(d.formations || []), { diplome: '', ecole: '', annee: '' }] }))}><Plus size={13} /> Ajouter</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {(cvData.formations || []).map((f, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
                      <div className="input-group"><label className="input-label">Diplôme</label><input className="input" value={f.diplome || ''} onChange={(e) => {
                        const fms = [...cvData.formations];
                        fms[i] = { ...fms[i], diplome: e.target.value };
                        setCvData((d) => ({ ...d, formations: fms }));
                      }} /></div>
                      <div className="input-group"><label className="input-label">École</label><input className="input" value={f.ecole || ''} onChange={(e) => {
                        const fms = [...cvData.formations];
                        fms[i] = { ...fms[i], ecole: e.target.value };
                        setCvData((d) => ({ ...d, formations: fms }));
                      }} /></div>
                      <div className="input-group"><label className="input-label">Année</label><input className="input" value={f.annee || ''} onChange={(e) => {
                        const fms = [...cvData.formations];
                        fms[i] = { ...fms[i], annee: e.target.value };
                        setCvData((d) => ({ ...d, formations: fms }));
                      }} /></div>
                      <button className="btn btn-danger btn-icon btn-sm" style={{ marginBottom: 1 }} onClick={() => setCvData((d) => ({ ...d, formations: d.formations.filter((_, j) => j !== i) }))}><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>


        {(step === 'edit' || step === 'done') && (
          <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setStep('upload'); setFile(null); setCvData(null); setCvId(null); }}>
              <RefreshCw size={14} /> Recommencer
            </button>
            <div style={{ display: 'flex', gap: 10 }}>
              {step === 'done' && cvId && onRescore && (
                <button className="btn btn-secondary" onClick={handleRescore} disabled={rescoring} title="Relancer le scoring IA avec ce CV converti">
                  {rescoring ? <div className="spinner" style={{ width: 13, height: 13 }} /> : <RefreshCw size={13} />}
                  {rescoring ? 'Rescore…' : 'Rescorer'}
                </button>
              )}
              {cvId && (
                <>
                  <button className="btn btn-secondary" onClick={handleDownload}><Download size={14} /> Télécharger DOCX</button>
                  <button className="btn btn-secondary" onClick={() => setSendModal(true)}><Send size={14} /> Envoyer</button>
                  <button className="btn btn-secondary" onClick={handleTranslate} disabled={translating} title="Traduire le CV en anglais">
                    {translating ? <div className="spinner" style={{ width: 13, height: 13 }} /> : <Globe size={13} />}
                    {translating ? 'Traduction…' : 'Anglais'}
                  </button>
                </>
              )}
              <button className="btn btn-orange" onClick={handleSave} disabled={loading} style={{ background: '#3B82F6', borderColor: '#3B82F6' }}>
                {loading ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <Save size={14} />}
                {loading ? 'Enregistrement…' : 'Enregistrer le CV Wikeys'}
              </button>
            </div>
          </div>
        )}
      </div>


      {sendModal && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={(e) => e.target === e.currentTarget && setSendModal(false)}
        >
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3>Envoyer au candidat</h3>
              <button className="btn btn-ghost btn-icon" onClick={() => setSendModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="input-group">
                <label className="input-label">Email du candidat *</label>
                <input className="input" type="email" placeholder="candidat@email.com" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} />
              </div>
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--blue-50)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem', color: 'var(--blue-700)' }}>
                💡 Le candidat recevra un lien sécurisé pour réviser son CV.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSendModal(false)}>Annuler</button>
              <button className="btn btn-orange" onClick={handleSend} disabled={!sendEmail}><Send size={14} /> Envoyer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
